import { useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { displayName } from '../people';
import VendorTag from './VendorTag';
import { groupProblem, membersOf, type Group } from '../groups';
import Icon from './Icon';

/**
 * TEAMS, AND THE TWO QUESTIONS EACH ONE ANSWERS.
 *
 * A group is only useful in both directions at once — who is in it, and what
 * it opens — and separating them across two screens is how somebody puts
 * three people in a team that is named on no boards and wonders why nothing
 * happened. So one screen, one group at a time, with both lists side by side.
 *
 * WHY IT IS NOT ON THE ACCESS TAB. That tab answers "what can this PERSON
 * reach", one person at a time. This answers "who is in this TEAM, and where
 * does it reach". Same subject, opposite direction; putting both on one screen
 * produced the matrix that had to be thrown away.
 */
export default function Groups({ report }: { report: (p: Promise<unknown>) => void }) {
  const groups = useApp((s) => s.groups);
  const groupsOf = useApp((s) => s.groupsOf);
  const access = useApp((s) => s.access);
  const profiles = useApp((s) => s.profiles);
  const projects = useApp((s) => s.projects);
  const createGroup = useApp((s) => s.createGroup);
  const renameGroup = useApp((s) => s.renameGroup);
  const deleteGroup = useApp((s) => s.deleteGroup);
  const setInGroup = useApp((s) => s.setInGroup);
  const setProjectGroups = useApp((s) => s.setProjectGroups);

  const [newName, setNewName] = useState('');
  const [problem, setProblem] = useState('');
  const [open, setOpen] = useState('');
  /** Narrows the people list inside an open team. */
  const [find, setFind] = useState('');

  const add = () => {
    const wrong = groupProblem(newName, groups);
    if (wrong) return setProblem(wrong);
    setProblem('');
    report(createGroup(newName).then((id) => { setNewName(''); setOpen(id); }));
  };

  /** One group, expanded: who is in it, and which boards it opens. */
  const panel = (g: Group) => {
    const inIt = membersOf(groupsOf, g.id);
    const opens = projects.filter((p) => (p.groups ?? []).includes(g.id));
    const shown = access.emails.filter(
      (e) =>
        !find.trim() ||
        e.toLowerCase().includes(find.trim().toLowerCase()) ||
        displayName(e, profiles).toLowerCase().includes(find.trim().toLowerCase())
    );
    const allShownIn = shown.length > 0 && shown.every((e) => inIt.includes(e));
    return (
      <div className="person-manage">
        <div className="two-up">
          <div>
            <div className="grp-heading">
              Who is in it
              {/* SELECT ALL, ON WHAT IS SHOWN — which is the whole point of
                  having a filter beside it. Forty developers become: type
                  "dev", press Add all. Ticking forty boxes is not a feature. */}
              {shown.length > 1 && (
                <button
                  className="btn btn-small grp-all"
                  onClick={() =>
                    report(
                      Promise.all(
                        shown
                          .filter((e) => inIt.includes(e) === allShownIn)
                          .map((e) => setInGroup(e, g.id, !allShownIn))
                      )
                    )
                  }
                >
                  {allShownIn ? 'Remove all shown' : `Add all ${shown.length} shown`}
                </button>
              )}
            </div>
            {/* Offered once the list is long enough that finding somebody is
                work. Below that it is a box with nothing to do. */}
            {access.emails.length > 8 && (
              <input
                className="input grp-find"
                value={find}
                onChange={(e) => setFind(e.target.value)}
                placeholder="Find a person"
              />
            )}
            <div className="grp-list">
              {/* Same two empties as the People list — see Admin.tsx. An
                  untouched search box must not be quoted back at somebody. */}
              {shown.length === 0 && (
                <p className="hint">
                  {find.trim()
                    ? `Nobody matches “${find}”.`
                    : 'Everybody in the workspace is already in this team.'}
                </p>
              )}
              {shown.map((e) => (
                <label key={e} className="grp-row">
                  <input
                    type="checkbox"
                    checked={inIt.includes(e)}
                    onChange={(ev) => report(setInGroup(e, g.id, ev.target.checked))}
                  />
                  <span className="grp-name">{displayName(e, profiles)}<VendorTag who={e} profiles={profiles} compact /></span>
                  <span className="grp-sub">{e}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="grp-heading">Boards it opens</div>
            <div className="grp-list">
              {projects.length === 0 && <p className="hint">There are no boards yet.</p>}
              {projects.map((p) => {
                const on = (p.groups ?? []).includes(g.id);
                return (
                  <label key={p.id} className="grp-row">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        report(
                          setProjectGroups(
                            p.id,
                            on
                              ? (p.groups ?? []).filter((x) => x !== g.id)
                              : [...(p.groups ?? []), g.id]
                          )
                        )
                      }
                    />
                    <span className="project-badge">{p.key}</span>
                    <span className="grp-name">{p.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* SAID WHERE IT IS TRUE, not as a general warning. A group that opens
            nothing is the commonest way this feature does nothing at all: three
            people added to a team named on no boards, and no error anywhere. */}
        {inIt.length > 0 && opens.length === 0 && (
          <p className="grid-warning" role="alert">
            {inIt.length} {inIt.length === 1 ? 'person is' : 'people are'} in {g.name} and it opens
            no boards, so it is granting nothing. Tick a board on the right.
          </p>
        )}

        <div className="grp-foot">
          <label className="stack grp-rename">
            Name
            <input
              className="input"
              defaultValue={g.name}
              onBlur={(ev) => {
                const v = ev.target.value.trim();
                if (v && v !== g.name) report(renameGroup(g.id, v));
              }}
            />
          </label>
        </div>
      </div>
    );
  };

  return (
    <section className="panel">
      <h2>Teams</h2>
      <p className="hint">
        A team is named on a board once, and everybody in it can open that board. It saves setting
        the same thing up for each person — and it keeps working when somebody joins or leaves.
      </p>

      <div className="people">
        {groups.length === 0 && (
          <p className="hint">
            No teams yet. Make one below — <em>Developers</em>, <em>Design</em>, whatever matches how
            you actually work.
          </p>
        )}
        {groups.map((g) => {
          const inIt = membersOf(groupsOf, g.id);
          const opens = projects.filter((p) => (p.groups ?? []).includes(g.id)).length;
          const isOpen = open === g.id;
          return (
            <div key={g.id} className={`person-card ${isOpen ? 'is-open' : ''}`}>
              <div className="person-top">
                <span className="avatar-me person-av">
                  <Icon name="users" size={14} />
                </span>
                <div className="person-id">
                  <div className="person-name">{g.name}</div>
                  {/* Both numbers, because either alone is misleading: a team of
                      six that opens nothing grants nothing, and a team on four
                      boards with nobody in it grants nothing either. */}
                  <div className="person-sub">
                    {inIt.length} {inIt.length === 1 ? 'person' : 'people'} · opens {opens}{' '}
                    {opens === 1 ? 'board' : 'boards'}
                  </div>
                </div>
                {/* BESIDE THE NAME, not at the foot of the panel. It was in a
                    row of its own under the two lists, which put the most
                    destructive control on the screen furthest from the thing it
                    destroys — and left it looking unaligned with everything
                    else. It belongs with the team it removes. */}
                {isOpen && (
                  <button
                    className="btn btn-small btn-danger"
                    onClick={async () => {
                      /* Named in the question, because "are you sure" answers
                         nothing. Deleting takes the group off every person and
                         every board, and somebody should know how much that is
                         before pressing it. */
                      const ok = await confirmDialog({
                        title: `Remove the ${g.name} group?`,
                        body: `${inIt.length} ${inIt.length === 1 ? 'person' : 'people'} would lose whatever access it gave them on ${opens} ${opens === 1 ? 'board' : 'boards'}. Nobody is removed from the workspace.`,
                        confirmLabel: 'Remove',
                        tone: 'danger',
                      });
                      if (ok) report(deleteGroup(g.id));
                    }}
                  >
                    Remove team
                  </button>
                )}
                <button className="btn btn-small" onClick={() => setOpen(isOpen ? '' : g.id)} aria-expanded={isOpen}>
                  {isOpen ? 'Done' : 'Manage'}
                </button>
              </div>
              {isOpen && panel(g)}
            </div>
          );
        })}
      </div>

      {problem && <div className="login-error" role="alert">{problem}</div>}

      <div className="comment-box grp-add">
        <input
          className="input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Developers"
        />
        <button className="btn btn-primary" onClick={add}>
          Make a team
        </button>
      </div>
    </section>
  );
}
